"use client";

import React, { createContext, useContext, ReactNode } from "react";

export interface BrandingConfig {
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  fontFamily?: string;
  companyName?: string;
}

export interface FeatureFlags {
  designsPerDay: number; // -1 for unlimited
  stylesAvailable: string[] | "all";
  seasonalViews: boolean;
  realTimeEditing: boolean;
  savedDesigns: number; // -1 for unlimited
  emailRequired: boolean;
  inventoryIntegration: boolean;
  clientManagement: boolean;
  proposalGeneration: boolean;
  multiUser: boolean;
  customBranding: boolean;
  analytics: "none" | "basic" | "advanced";
  // "Recommended Supplies" on the results page. Off for D2C until the links go
  // somewhere purchasable (affiliate partners) — PLA2-764.
  productRecommendations: boolean;
}

export interface TenantContext {
  type: "consumer" | "garden_center" | "landscaper";
  retailerId?: string;
  proAccountId?: string;
  branding: BrandingConfig;
  features: FeatureFlags;
}

const TenantContext = createContext<TenantContext | null>(null);

export function TenantProvider({
  children,
  tenant,
}: {
  children: ReactNode;
  tenant: TenantContext;
}) {
  return (
    <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>
  );
}

export function useTenantContext(): TenantContext {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenantContext must be used within a TenantProvider");
  }
  return context;
}

export function useFeatures(): FeatureFlags {
  const { features } = useTenantContext();
  return features;
}

export function useBranding(): BrandingConfig {
  const { branding } = useTenantContext();
  return branding;
}
